import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Fragment, useState } from "react";
import { Dialog, Disclosure, Menu, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  ChevronDownIcon,
  FunnelIcon,
  MinusIcon,
  PlusIcon,
  Squares2X2Icon,
} from "@heroicons/react/20/solid";
import Pagination from "./Pagination";
import ProductList from "./ProductList";
import { fetchAllCategoriesAsync } from "../slices/CategoryListSlice";
import { fetchAllBrandsAsync } from "../slices/BrandListSlice";
import {
  fetchAllProductsAsync,
  filterProductsAsync,
} from "../slices/ProductListSlice";
import Spinner from "./Spinner";
import { PAGE_LIMIT } from "../app/constants";

const sortOptions = [
  { name: "Best Rating", sort: "rating", order: "desc", current: false },
  { name: "Price: Low to High", sort: "price", order: "asc", current: false },
  { name: "Price: High to Low", sort: "price", order: "desc", current: false },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const Product = () => {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const productTop = useRef(null);
  const dispatch = useDispatch();
  const totalItems = useSelector((state) => state.productList.totalItems);
  let categories = useSelector((state) => state.categoryList.categories);
  const categoriesStatus = useSelector((state) => state.categoryList.status);
  let brands = useSelector((state) => state.brandList.brands);
  const brandsStatus = useSelector((state) => state.brandList.status);
  const [filterTags, setFilterTags] = useState([]);
  let pages = 1;
  const [page, setPage] = useState(pages);
  let filters = [
    {
      id: "brand",
      name: "Brand",
      options: brands,
    },
    {
      id: "category",
      name: "Category",
      options: categories,
    },
  ];
  // fetching category list and dispatching fetch actions here
  useEffect(() => {
    dispatch(fetchAllCategoriesAsync());
    dispatch(fetchAllBrandsAsync());
  }, [dispatch]);

  //Filter Logic
  const handleFilter = (e, section, option) => {
    const filteredArray = filterTags.filter(
      (obj) => !("_page" in obj || "_limit" in obj)
    );
    let tempFilter = [...filteredArray];
    if (e.target.checked) {
      let optionVal = option.value;
      tempFilter.push({ [section.id]: optionVal });
    } else {
      tempFilter = tempFilter.filter((item) => {
        for (const key in item) {
          return item[key] !== option.value;
        }
      });
    }
    tempFilter = [...tempFilter, { _page: pages, _limit: PAGE_LIMIT }];
    setFilterTags(tempFilter);
    dispatch(filterProductsAsync(tempFilter));
    // console.log(tempFilter)
  };
  //Sort Logic
  const handleSort = (e, option) => {
    const filteredArray = filterTags.filter(
      (obj) => !("_sort" in obj || "_order" in obj)
    );
    const newSort = [
      ...filteredArray,
      { _sort: option.sort, _order: option.order },
    ];
    setFilterTags(newSort);
    dispatch(filterProductsAsync(newSort));
  };
  //Clear Filters
  const clearFilter = (e, section) => {
    let tempFilter = [...filterTags];
    tempFilter = tempFilter.filter((item) => {
      for (const key in item) {
        return key !== section.id;
      }
    });
    setFilterTags(tempFilter);
    dispatch(filterProductsAsync(tempFilter));
  };
  //Clear Sort
  const clearSort = () => {
    const filteredArray = filterTags.filter(
      (obj) => !("_sort" in obj || "_order" in obj)
    );
    setFilterTags(filteredArray);
    dispatch(filterProductsAsync(filteredArray));
  };
  //handlePAgeChange
  const pageChange = (page) => {
    //for removing old page and limit from tags
    const filteredArray = filterTags.filter(
      (obj) => !("_page" in obj || "_limit" in obj)
    );
    // adding new page and limit into tags
    let tempFilter = [...filteredArray, { _page: page, _limit: PAGE_LIMIT }];
    setFilterTags(tempFilter);
    pages = page;
    setPage(page);
    dispatch(filterProductsAsync(tempFilter));
  };
  // reset page when filter  is applied so that it again move to 1st page instead of any current page
  useEffect(() => {
    pages = 1;
    setPage(1);
  }, [totalItems]);
  return (
    <>
      <div className="bg-white">
        <div>
          {/* Mobile filter dialog */}
          <Transition.Root show={mobileFiltersOpen} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-40 lg:hidden"
              onClose={setMobileFiltersOpen}
            >
              <Transition.Child
                as={Fragment}
                enter="transition-opacity ease-linear duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black bg-opacity-25" />
              </Transition.Child>

              <div className="fixed inset-0 z-40 flex">
                <Transition.Child
                  as={Fragment}
                  enter="transition ease-in-out duration-300 transform"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transition ease-in-out duration-300 transform"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl">
                    <div className="flex items-center justify-between px-4">
                      <h2 className="text-lg font-medium text-gray-900">
                        Filters
                      </h2>
                      <button
                        type="button"
                        className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                        onClick={() => setMobileFiltersOpen(false)}
                      >
                        <span className="sr-only">Close menu</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    {/* Filters */}
                    <form className="mt-4 border-t border-gray-200">
                      <h3 className="sr-only">Categories</h3>

                      {filters.map((section) => (
                        <Disclosure
                          as="div"
                          key={section.id}
                          className="border-t border-gray-200 px-4 py-6"
                        >
                          {({ open }) => (
                            <>
                              <h3 className="-mx-2 -my-3 flow-root">
                                <Disclosure.Button className="flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                                  <span className="font-medium text-gray-900">
                                    {section.name}
                                  </span>
                                  <span className="ml-6 flex items-center">
                                    {open ? (
                                      <MinusIcon
                                        className="h-5 w-5"
                                        aria-hidden="true"
                                        onClick={(e) => clearFilter(e, section)}
                                      />
                                    ) : (
                                      <PlusIcon
                                        className="h-5 w-5"
                                        aria-hidden="true"
                                      />
                                    )}
                                  </span>
                                </Disclosure.Button>
                              </h3>
                              <Disclosure.Panel className="pt-6">
                                <div className="space-y-6">
                                  {categoriesStatus === "loading" ||
                                  brandsStatus === "loading" ? (
                                    <Spinner />
                                  ) : (
                                    section.options.map((option, optionIdx) => (
                                      <div
                                        key={option.value}
                                        className="flex items-center cursor-pointer"
                                      >
                                        <input
                                          id={`filter-mobile-${section.id}-${optionIdx}`}
                                          name={`${section.id}[]`}
                                          defaultValue={option.value}
                                          type="checkbox"
                                          defaultChecked={option.checked}
                                          onChange={(e) =>
                                            handleFilter(e, section, option)
                                          }
                                          className="cursor-pointer h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <label
                                          htmlFor={`filter-mobile-${section.id}-${optionIdx}`}
                                          className="ml-3 min-w-0 flex-1 text-gray-500 cursor-pointer"
                                        >
                                          {option.label}
                                        </label>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </Disclosure.Panel>
                            </>
                          )}
                        </Disclosure>
                      ))}
                    </form>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition.Root>

          <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-baseline justify-between border-b border-gray-200 pb-6 pt-3">
              <h1
                className="text-4xl font-bold tracking-tight text-gray-900"
                ref={productTop}
              >
                Trending
              </h1>

              <div className="flex items-center">
                <Menu as="div" className="relative inline-block text-left">
                  <div>
                    <Menu.Button className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                      Sort
                      <ChevronDownIcon
                        className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                    </Menu.Button>
                  </div>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                        {sortOptions.map((option) => (
                          <Menu.Item key={option.name}>
                            {({ active }) => (
                              <p
                                onClick={(e) => handleSort(e, option)}
                                className={classNames(
                                  option.current
                                    ? "font-medium text-gray-900 cursor-pointer"
                                    : "text-gray-500",
                                  active ? "bg-gray-100 cursor-pointer" : "",
                                  "block px-4 py-2 text-sm cursor-pointer"
                                )}
                              >
                                {option.name}
                              </p>
                            )}
                          </Menu.Item>
                        ))}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>

                <button
                  type="button"
                  className="-m-2 ml-5 p-2 text-gray-400 hover:text-gray-500 sm:ml-7"
                >
                  <span className="sr-only">View grid</span>
                  <Squares2X2Icon
                    onClick={() => clearSort()}
                    className="h-5 w-5"
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  className="-m-2 ml-4 p-2 text-gray-400 hover:text-gray-500 sm:ml-6 lg:hidden"
                  onClick={() => setMobileFiltersOpen(true)}
                >
                  <span className="sr-only">Filters</span>
                  <FunnelIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>

            <section aria-labelledby="products-heading" className="pb-24 pt-6">
              <h2 id="products-heading" className="sr-only">
                Products
              </h2>

              <div className="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-4">
                {/* Filters */}
                <form className="hidden lg:block">
                  <h3 className="sr-only">Categories</h3>
                  {filters.map((section) => (
                    <Disclosure
                      as="div"
                      key={section.id}
                      className="border-b border-gray-200 py-6"
                    >
                      {({ open }) => (
                        <>
                          <h3 className="-my-3 flow-root">
                            <Disclosure.Button className="flex w-full items-center justify-between bg-white rounded-md px-2 hover:bg-gray-100 py-3 text-sm text-gray-400 hover:text-gray-500">
                              <span className="font-medium text-gray-900">
                                {section.name}
                              </span>
                              <span className="ml-6 flex items-center">
                                {open ? (
                                  <MinusIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                    onClick={(e) => clearFilter(e, section)}
                                  />
                                ) : (
                                  <PlusIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                )}
                              </span>
                            </Disclosure.Button>
                          </h3>
                          <Disclosure.Panel className="pt-6">
                            <div className="space-y-4">
                              {categoriesStatus === "loading" ||
                              brandsStatus === "loading" ? (
                                <Spinner />
                              ) : (
                                section.options.map((option, optionIdx) => (
                                  <div
                                    key={option.value}
                                    className="flex items-center cursor-pointer"
                                  >
                                    <input
                                      id={`filter-${section.id}-${optionIdx}`}
                                      name={`${section.id}[]`}
                                      defaultValue={option.value}
                                      type="checkbox"
                                      defaultChecked={option.checked}
                                      onChange={(e) =>
                                        handleFilter(e, section, option)
                                      }
                                      className="cursor-pointer h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <label
                                      htmlFor={`filter-${section.id}-${optionIdx}`}
                                      className="cursor-pointer ml-3 text-sm text-gray-600"
                                    >
                                      {option.label}
                                    </label>
                                  </div>
                                ))
                              )}
                            </div>
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </form>
                {/* Product grid */}
                <div className="lg:col-span-3">
                  <ProductList />
                </div>
              </div>
            </section>
          </main>
          <Pagination
            page={page}
            productTop={productTop}
            totalItems={totalItems}
            pageChange={pageChange}
          />
        </div>
      </div>
    </>
  );
};

export default Product;
